import { lazy, ReactNode, Suspense, useEffect, useState } from "react";
import {
  createBrowserRouter,
  createHashRouter,
  matchRoutes,
  Navigate,
  Outlet,
  useLocation,
} from "react-router-dom";
import { login } from "@onmo/auth";
import { clearProfileSession } from "@onmo/graphql";
import { PageRoutes, SUBSCRIPTION_PATHS } from "../constants/routes";
import { getRoutePath, getRouteSearch } from "../models/location";
import { getPageByPath } from "../models/navigation";
import { useTranslation } from "react-i18next";
import { SettingsView } from "../constants/settings";
import { getFullscreenStorageKey, STORAGE_FULLSCREEN } from "../models/storage";
import Console from "../pages/Console/Console";
import EssentialPack from "../pages/EssentialPack/EssentialPack";
import InstallGame from "../pages/InstallGame/InstallGame";
import { MainLayout } from "../components/common/MainLayout/MainLayout";
import { ProtectedRoute } from "./ProtectedRoute";
import { GamepadProvider } from "../contexts/GamepadProvider";
import { useFullscreenOnFirstTouch } from "../hooks/useFullscreenOnFirstTouch";
import { LoadingScreen } from "../components/common/LoadingScreen/LoadingScreen";
import Paywall from "../pages/Paywall/Paywall";
import Payment from "../pages/Payment/Payment";
import { TvRedirect } from "../pages/TvRedirect/TvRedirect";
import ErrorNetwork from "../pages/ErrorNetwork/ErrorNetwork";
import { useErrorNetworkRedirect } from "../hooks/useErrorNetworkRedirect";
import {
  useCurrentProfile,
  useIsKidProfile,
  useIsMainProfile,
  useProfilePickerRedirect,
} from "../hooks/useProfileSession";
import { setManifest } from "../models/manifest";
import { cache } from "../constants/pwa";
import { BeforeInstallPromptEvent } from "../types/screen";
import { useResizeScreen } from "../hooks/useResizeScreen";
import { restoreDefaultOrientation } from "../utils/orientation";
import { getActivationCode, isShowActivation } from "../models/storage";
import {
  isAndroidApp,
  isHashRouting,
  isSubscriptionDisabled,
} from "../utils/platform";
import { SubscriptionRequiredPopup } from "../components/common/SubscriptionRequiredPopup";
import { CookieConsentPopup } from "../components/common/CookieConsentPopup";

type RouteConfig = {
  path: string;
  element: ReactNode;
  layout?: "main";
};

const Search = lazy(() => import("../pages/Search/Search"));
const GameDetails = lazy(() => import("../pages/GameDetails/GameDetails"));
const MyGames = lazy(() => import("../pages/MyGames/MyGames"));
const SettingSection = lazy(
  () => import("../components/common/SettingSection/SettingSection"),
);
const Activation = lazy(() => import("../pages/Activation/Activation"));
const Settings = lazy(() => import("../pages/Settings/Settings"));
const GamePlay = lazy(() => import("../pages/GamePlay/GamePlay"));
const LoginQR = lazy(() => import("../pages/LoginQR/LoginQR"));
const Onboarding = lazy(() => import("../pages/Onboarding/Onboarding"));
const ProfileInfo = lazy(() => import("../pages/ProfileInfo/ProfileInfo"));
const Subscription = lazy(() => import("../pages/Subscription/Subscription"));
const WhoIsPlaying = lazy(() => import("../pages/WhoIsPlaying/WhoIsPlaying"));

const NotForKids = ({ children }: { children: JSX.Element }) =>
  useIsKidProfile() ? <Navigate to={PageRoutes.Home} replace /> : children;

const SubscriptionEnabled = ({ children }: { children: JSX.Element }) => {
  const isMainProfile = useIsMainProfile();

  return isSubscriptionDisabled || !isMainProfile ? (
    <Navigate to={PageRoutes.Home} replace />
  ) : (
    children
  );
};

// index.html title (VITE_APP_TITLE)
const APP_TITLE = document.title;

//  Shared layout
const SharedMainLayout = () => (
  <MainLayout>
    <Suspense fallback={<LoadingScreen />}>
      <Outlet />
    </Suspense>
  </MainLayout>
);

const ROUTES_GAME_SECTION: RouteConfig[] = [
  {
    path: PageRoutes.EssentialPass,
    element: <EssentialPack />,
    layout: "main",
  },
  {
    path: PageRoutes.InstallGame,
    element: (
      <NotForKids>
        <InstallGame />
      </NotForKids>
    ),
    layout: "main",
  },
];

const ROUTES_AUTH_ALL: RouteConfig[] = [
  { path: PageRoutes.Home, element: <Console />, layout: "main" },
  { path: PageRoutes.MyGames, element: <MyGames />, layout: "main" },
  { path: PageRoutes.Search, element: <Search />, layout: "main" },
  {
    path: PageRoutes.Settings,
    element: <Settings />,
    layout: "main",
  },
  ...ROUTES_GAME_SECTION,
  {
    path: PageRoutes.Activate,
    element: <Activation />,
  },
  {
    path: PageRoutes.Paywall,
    element: (
      <SubscriptionEnabled>
        <Paywall />
      </SubscriptionEnabled>
    ),
  },
  {
    path: PageRoutes.Payment,
    element: (
      <SubscriptionEnabled>
        <Payment />
      </SubscriptionEnabled>
    ),
  },
  {
    path: PageRoutes.General,
    element: <SettingSection type={SettingsView.General} />,
  },
  {
    path: PageRoutes.LegalInformation,
    element: <SettingSection type={SettingsView.LegalInformation} />,
  },
  {
    path: PageRoutes.CookieSettings,
    element: <SettingSection type={SettingsView.CookieSettings} />,
  },
  {
    path: PageRoutes.OpenSource,
    element: <SettingSection type={SettingsView.OpenSource} />,
  },
  {
    path: `${PageRoutes.OpenSource}/:slug`,
    element: <SettingSection type={SettingsView.OpenSource} />,
  },
  {
    path: PageRoutes.Privacy,
    element: <SettingSection type={SettingsView.Privacy} />,
  },
  {
    path: PageRoutes.Tnc,
    element: <SettingSection type={SettingsView.Tnc} />,
  },
  {
    path: PageRoutes.CookiePolicy,
    element: <SettingSection type={SettingsView.CookiePolicy} />,
  },
  {
    path: PageRoutes.Support,
    element: <SettingSection type={SettingsView.Support} />,
  },
  {
    path: PageRoutes.Exit,
    element: <SettingSection type={SettingsView.Exit} />,
  },
  {
    path: PageRoutes.ProfileInfo,
    element: <ProfileInfo />,
  },
  {
    path: PageRoutes.WhoIsPlaying,
    element: <WhoIsPlaying />,
  },
  {
    path: `${PageRoutes.GamePlay}/:id`,
    element: <GamePlay />,
  },
  {
    path: `${PageRoutes.Game}/:id`,
    element: <GameDetails />,
    layout: "main",
  },
  {
    path: PageRoutes.Subscription,
    element: (
      <SubscriptionEnabled>
        <Subscription />
      </SubscriptionEnabled>
    ),
  },
  {
    path: PageRoutes.ErrorNetwork,
    element: <ErrorNetwork />,
  },
  {
    path: PageRoutes.StreamingProfile,
    element: <SettingSection type={SettingsView.StreamingProfile} />,
  },
];

// Deep links to the removed routes fall through to the "*" route and land back
// on the home page.
const ROUTES_AUTH: RouteConfig[] = isSubscriptionDisabled
  ? ROUTES_AUTH_ALL.filter((route) => !SUBSCRIPTION_PATHS.includes(route.path))
  : ROUTES_AUTH_ALL;

const ROUTES_NOT_AUTH: RouteConfig[] = [
  { path: PageRoutes.Home, element: <Console />, layout: "main" },
  ...ROUTES_GAME_SECTION,
  {
    path: `${PageRoutes.Game}/:id`,
    element: <GameDetails />,
    layout: "main",
  },
  {
    path: PageRoutes.Onboarding,
    element: <Onboarding />,
  },
  {
    path: PageRoutes.LoginQR,
    element: <LoginQR />,
  },
  {
    path: PageRoutes.ErrorNetwork,
    element: <ErrorNetwork />,
  },
];

const ROUTES_ONBOARDING: RouteConfig[] = [
  {
    path: PageRoutes.Onboarding,
    element: <Onboarding />,
  },
  ...ROUTES_GAME_SECTION,
  {
    path: PageRoutes.ErrorNetwork,
    element: <ErrorNetwork />,
  },
];

const ROUTES_PUBLIC: RouteConfig[] = [
  {
    path: PageRoutes.Tv,
    element: <TvRedirect />,
  },
];

const PUBLIC_PATHS = new Set(
  [...ROUTES_NOT_AUTH, ...ROUTES_PUBLIC, ...ROUTES_ONBOARDING].map(
    (route) => route.path,
  ),
);

// We need to filter routes do not need to auth then go back to onboarding page
const ROUTES_REQUIRED_AUTH = ROUTES_AUTH.filter(
  (route) => !PUBLIC_PATHS.has(route.path),
).map((r) => ({ path: r.path }));

const AuthFallbackLogin = () => {
  const location = useLocation();
  const needsAuth = !!matchRoutes(ROUTES_REQUIRED_AUTH, location);
  const reLogin = needsAuth && !isAndroidApp;

  useEffect(() => {
    if (reLogin) {
      clearProfileSession();
      login();
    }
  }, [reLogin]);

  if (reLogin) return <LoadingScreen />;
  return <Navigate to={PageRoutes.Onboarding} replace />;
};

const RootLayout = () => {
  useResizeScreen();
  useErrorNetworkRedirect();
  const isDecidingProfile = useProfilePickerRedirect();
  const { currentProfile } = useCurrentProfile();
  const isRuntimeSubscriptionDisabled =
    isSubscriptionDisabled || currentProfile?.isOwner === false;
  const fullscreenStorageKey = getFullscreenStorageKey(currentProfile?.id);

  const [isFull, setIsFull] = useState(
    () => localStorage.getItem(fullscreenStorageKey) !== "false",
  );

  useEffect(() => {
    const sync = () => {
      setIsFull(localStorage.getItem(fullscreenStorageKey) === "true");
    };
    sync();
    window.addEventListener(STORAGE_FULLSCREEN, sync);
    setManifest(
      import.meta.env.VITE_APP_TITLE,
      `${window.location.protocol}//${window.location.host}`,
    );
    // On Android lock to portrait by default except tablets
    restoreDefaultOrientation();

    const onBeforeinstallprompt = (e: Event) => {
      const event = e as BeforeInstallPromptEvent;
      event.preventDefault();
      cache["root"] = event;
    };
    window.addEventListener("beforeinstallprompt", onBeforeinstallprompt);
    return () => {
      window.removeEventListener(STORAGE_FULLSCREEN, sync);
      window.removeEventListener("beforeinstallprompt", onBeforeinstallprompt);
    };
  }, [fullscreenStorageKey]);
  const location = useLocation();
  const isOnboarding = location.pathname === PageRoutes.Onboarding;
  useFullscreenOnFirstTouch(isFull && !isOnboarding);

  const { t } = useTranslation();
  const pageKey = getPageByPath(location.pathname);
  useEffect(() => {
    document.title = pageKey ? `${t(pageKey)} | ${APP_TITLE}` : APP_TITLE;
  }, [pageKey, t]);

  return (
    <GamepadProvider>
      <Suspense fallback={<LoadingScreen />}>
        {isDecidingProfile ? <LoadingScreen /> : <Outlet />}
      </Suspense>
      {isRuntimeSubscriptionDisabled && <SubscriptionRequiredPopup />}
      <CookieConsentPopup />
    </GamepadProvider>
  );
};

const createRouter = isHashRouting ? createHashRouter : createBrowserRouter;

export const routeDefs = (isAuthenticate = false, isOnboarded = true) => {
  const routes = isAuthenticate
    ? ROUTES_AUTH
    : isOnboarded
    ? ROUTES_NOT_AUTH
    : ROUTES_ONBOARDING;

  const mainLayoutRoutes = routes.filter((r) => r.layout === "main");
  const standaloneRoutes = routes.filter((r) => !r.layout);

  let fallbackPath: PageRoutes | string = isAuthenticate
    ? PageRoutes.Home
    : PageRoutes.Onboarding;

  const REDIRECT_AFTER_LOGIN_PATHS: string[] = [
    ...(isSubscriptionDisabled ? [] : [PageRoutes.Payment]),
    PageRoutes.Support,
    PageRoutes.Privacy,
    PageRoutes.Tnc,
  ];

  const routePath = getRoutePath();
  if (!isAuthenticate && REDIRECT_AFTER_LOGIN_PATHS.includes(routePath)) {
    localStorage.setItem("activeLink", routePath + getRouteSearch());
  }
  const activeLink = localStorage.getItem("activeLink");
  // A link stored before the flag was flipped would point at a removed route
  // and bounce off the "*" fallback forever.
  const isRemovedLink =
    isSubscriptionDisabled &&
    SUBSCRIPTION_PATHS.some((path) => activeLink?.startsWith(path));
  if (isAuthenticate && activeLink && !isRemovedLink) {
    fallbackPath = activeLink;
  }
  if (isAuthenticate && isShowActivation()) {
    fallbackPath = `${PageRoutes.Activate}?code=${getActivationCode()}`;
  }

  return createRouter([
    // Public routes (no auth, no RootLayout)
    ...ROUTES_PUBLIC,
    {
      element: <RootLayout />,
      children: [
        {
          element: (
            <ProtectedRoute>
              <Outlet />
            </ProtectedRoute>
          ),
          children: [
            // Standalone routes (no MainLayout)
            ...standaloneRoutes,

            // Routes with SharedMainLayout
            ...(mainLayoutRoutes.length
              ? [{ element: <SharedMainLayout />, children: mainLayoutRoutes }]
              : []),

            // Fallback
            {
              path: "*",
              element: isAuthenticate ? (
                <Navigate to={fallbackPath} replace />
              ) : (
                <AuthFallbackLogin />
              ),
            },
          ],
        },
      ],
    },
  ]);
};
